/*!
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
import classNames from 'classnames';
import React, { useId } from 'react';

import { ButtonSpinner } from '../_common/ButtonSpinner';
import {
  type PolymorphicCommonProps,
  type TooltipObjectProps,
} from '../_common/types';
import { needleWarningMessage } from '../_common/utils';
import { type ButtonSizes } from '../button';
import { Kbd } from '../kbd';
import { Tooltip } from '../tooltip';

/**
 *
 *
 * Types
 *
 *
 */

export type IconButtonBaseProps = {
  children?: React.ReactNode;

  /** If the icon button is in disabled state */
  isDisabled?: boolean;

  /** If the button is doing something Async, it will display a loading spinner   */
  isLoading?: boolean;

  /** Size of button */
  size?: ButtonSizes;

  /** If the button is in floating state */
  isFloating?: boolean;

  /** Active - used for open context menus for example */
  isActive?: boolean;

  /** A string that will be shown as a tooltip when hovering over the button it also acts as an aria-label- {@link https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Attributes/aria-label} */
  description: string | null;

  /** The Kbd components that will be displayed in the tooltip after the description text, this does not include the keyboard shortcut functionality this only displays the Kbd components. */
  descriptionKbdProps?: React.ComponentProps<typeof Kbd>;

  /** Variant of the button */
  variant?: 'neutral' | 'danger';

  /** Variant of the button */
  iconButtonVariant?: 'default' | 'clean';

  /**
   * Props for the tooltip component.
   */
  tooltipProps?: TooltipObjectProps;

  /** Click handler */
  onClick?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;

  /** Accessible message for screen readers when the button is in a loading state */
  loadingMessage?: string;
};

export const IconButtonBase = <T extends React.ElementType = 'button'>({
  children,
  as,
  iconButtonVariant = 'default',
  isLoading = false,
  isDisabled = false,
  size = 'medium',
  isFloating = false,
  // Does not have a default value to avoid setting aria-pressed to true if not explicitly set
  isActive = undefined,
  description,
  descriptionKbdProps,
  tooltipProps,
  className,
  style,
  variant = 'neutral',
  htmlAttributes,
  onClick,
  ref,
  // TODO v5: maybe update default message to 'Loading'. This value is for backward compatibility with loading spinners old aria-label
  loadingMessage = 'Loading content',
  ...restProps
}: PolymorphicCommonProps<T, IconButtonBaseProps>) => {
  const Component: React.ElementType = as ?? 'button';
  const loadingId = useId();

  const isInteractable = !isDisabled && !isLoading;

  const isClean = iconButtonVariant === 'clean';
  const isDanger = variant === 'danger';

  const classes = classNames('ndl-icon-btn', className, {
    'ndl-active': Boolean(isActive),
    'ndl-clean': isClean,
    'ndl-danger': isDanger,
    'ndl-disabled': isDisabled,
    'ndl-floating': isFloating,
    'ndl-large': size === 'large',
    'ndl-loading': isLoading,
    'ndl-medium': size === 'medium',
    'ndl-small': size === 'small',
  });

  if (isClean && isFloating) {
    throw new Error(
      'BaseIconButton: Cannot use isFloating and iconButtonVariant="clean" at the same time.',
    );
  }

  if (!description && !htmlAttributes?.['aria-label']) {
    needleWarningMessage(
      'Icon buttons do not have text, be sure to include a description or an aria-label for screen readers link: https://dequeuniversity.com/rules/axe/4.4/button-name?application=axeAPI',
    );
  }

  const handleClick = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    if (!isInteractable) {
      // By default, a <button /> element with disabled set to true will not get its
      // on click handler called. To support the same behavior on <a /> tags we
      // swallow the event here when disabled, since the <a /> tag with disabled set to
      // true will have its onClick handler called
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    if (onClick) {
      onClick(event);
    }
  };

  const tooltipContent = React.useMemo(() => {
    const descriptionContent = description ?? tooltipProps?.content?.children;
    return (
      <>
        {descriptionContent}
        {descriptionKbdProps && <Kbd {...descriptionKbdProps} />}
      </>
    );
  }, [description, descriptionKbdProps, tooltipProps?.content?.children]);

  return (
    <Tooltip
      hoverDelay={{
        close: 0,
        open: 500,
      }}
      isDisabled={description === null || isDisabled}
      type="simple"
      {...tooltipProps?.root}
    >
      <Tooltip.Trigger {...tooltipProps?.trigger} hasButtonWrapper>
        <Component
          type="button"
          onClick={handleClick}
          disabled={isDisabled}
          aria-disabled={!isInteractable}
          aria-label={description}
          aria-pressed={isActive}
          className={classes}
          style={style}
          ref={ref}
          aria-describedby={isLoading ? loadingId : undefined}
          {...restProps}
          {...htmlAttributes}
        >
          <div className="ndl-icon-btn-inner">
            {isLoading ? (
              <ButtonSpinner
                loadingMessage={loadingMessage}
                size="small"
                htmlAttributes={{ id: loadingId }}
              />
            ) : (
              <div className="ndl-icon">{children}</div>
            )}
          </div>
        </Component>
      </Tooltip.Trigger>
      <Tooltip.Content {...tooltipProps?.content}>
        {tooltipContent}
      </Tooltip.Content>
    </Tooltip>
  );
};
